/**
 * A equipe do tenant para todo seletor e filtro de pessoa (pedido da PoolTrack,
 * 05/10) — `GET /users/team`. Nunca `GET /users`: aquela traz também os logins
 * de portal das associações, paginada, e fica para a tela de usuários. Uma
 * guarda de fonte (API, shared/team-picker-source.test.ts) reprova arquivo de
 * tela que volte a ela.
 *
 * Chave sob ['erp', 'users'] de propósito: criar ou editar usuário na tela de
 * usuários invalida esse prefixo, e a equipe dos seletores vem junto.
 */
import { queryOptions } from '@tanstack/react-query';
import {
  TEAM_LIST_MAX,
  TEAM_PICKER_LIMIT,
  type TeamListResponse,
  type TeamRole,
  type User,
} from '@erp/shared';
import { api, apiList } from '@/lib/api-client';

export const TEAM_KEY = ['erp', 'users', 'team'] as const;

interface TeamQuery {
  q?: string;
  includeInactive: boolean;
  limit: number;
}

async function fetchTeam(query: TeamQuery, signal: AbortSignal): Promise<TeamListResponse> {
  const res = await api<TeamListResponse>('/users/team', {
    signal,
    query: {
      q: query.q?.trim() || undefined,
      include_inactive: query.includeInactive ? 'true' : undefined,
      limit: query.limit,
    },
  });
  if (!res.error) return res.data;
  // Interruptor do lado rápido (mudança de dois lados): o SPA publica em ~3 min
  // e a API em ~25. Na janela em que a API no ar ainda não tem /users/team
  // (404), os seletores não podem ficar vazios — caem na lista antiga, SEM os
  // logins de portal. Sai quando a API com /users/team estiver em produção.
  if (res.error.status === 404) return legacyTeam(query, signal);
  throw res.error;
}

/** minúsculo e sem acento — a mesma dobra das buscas do SPA */
const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * O recuo da janela de publicação: GET /users (200 por nome) sem o portal, com
 * a busca e os inativos feitos aqui. Só roda com uma API sem /users/team, que
 * é anterior a F-108 — aí a lista ainda é global; a API nova recusa quem
 * estiver fora.
 */
async function legacyTeam(query: TeamQuery, signal: AbortSignal): Promise<TeamListResponse> {
  const res = await apiList<User>('/users', { query: { limit: TEAM_LIST_MAX }, signal });
  if (res.error) throw res.error;
  const q = fold(query.q?.trim() ?? '');
  const all = res.data.items.filter(
    (u) =>
      u.role !== 'client' &&
      (query.includeInactive || u.active) &&
      (q === '' || fold(`${u.name} ${u.email}`).includes(q))
  );
  return {
    items: all.slice(0, query.limit).map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role as TeamRole,
      active: u.active,
    })),
    hasMore: all.length > query.limit,
  };
}

/** O seletor: busca no servidor, 30 de cada vez, `hasMore` quando havia mais. */
export function teamSearchQuery(search: string, includeInactive: boolean) {
  return queryOptions({
    queryKey: [...TEAM_KEY, 'search', { search, includeInactive }] as const,
    queryFn: ({ signal }) =>
      fetchTeam({ q: search, includeInactive, limit: TEAM_PICKER_LIMIT }, signal),
    staleTime: 60_000,
  });
}

/**
 * A equipe inteira (até 200) — filtros por atendente, listas de marcar e o
 * nome de quem já está escolhido num formulário que só guarda o id. Sem
 * `includeInactive`, só os ativos.
 */
export function teamListQuery(opts: { includeInactive?: boolean } = {}) {
  const includeInactive = opts.includeInactive ?? false;
  return queryOptions({
    queryKey: [...TEAM_KEY, 'list', { includeInactive }] as const,
    queryFn: ({ signal }) => fetchTeam({ includeInactive, limit: TEAM_LIST_MAX }, signal),
    staleTime: 60_000,
  });
}
